import React from 'react';
import {Link} from 'react-router-dom';
import {ArrowRightOutlined, ClockCircleOutlined, FileDoneOutlined, UserOutlined} from '@ant-design/icons';
import {Fade} from 'react-awesome-reveal';
import {useTranslation} from 'react-i18next';
import classes from '../courses.module.css';
import IntlMessage from '../../../components/IntlMessage/IntlMessage';
import {CATEGORY_BY_CODE, FORMAT_BY_CODE} from '../courses.enums';
import useLocalizedText from '../hooks/useLocalizedText';
import useReducedMotion from '../hooks/useReducedMotion';
import {durationLabel} from '../courses.utils';
import {trainerPhoto} from '../img/trainers';

/**
 * Карточка курса в каталоге.
 *
 * Преподаватель показан намеренно: девять из двенадцати программ идут по 16 часов,
 * цены скрыты, и эксперт остаётся единственным, чем карточки реально различаются.
 */
const CourseCard = ({course, hint, index = 0}) => {
    const pick = useLocalizedText();
    const reduced = useReducedMotion();
    const {t} = useTranslation();

    const category = CATEGORY_BY_CODE[course.category];
    const format = FORMAT_BY_CODE[course.format];
    const photo = trainerPhoto(course.trainer?.photo);

    const card = (
        <Link to={`/courses/${course.slug}`} className={classes.card}>
            <div className={classes.cardTop}>
                {category && (
                    <span
                        className={classes.tagCategory}
                        style={{color: category.text, background: category.bg}}
                    >
                        <IntlMessage id={category.labelKey}/>
                    </span>
                )}
                {format && (
                    <span className={classes.chipFormat}>
                        <IntlMessage id={format.labelKey}/>
                    </span>
                )}
            </div>

            <h3 className={classes.cardTitle}>{pick(course.title)}</h3>
            <p className={classes.cardSubtitle}>{pick(course.subtitle)}</p>

            <div className={classes.cardMeta}>
                <div className={classes.cardMetaRow}>
                    <ClockCircleOutlined/>
                    <span className={classes.cardMetaStrong}>
                        {durationLabel(course.durationHours, pick(course.durationShape))}
                    </span>
                </div>
                <div className={classes.cardMetaRow}>
                    <FileDoneOutlined/>
                    {/* строка собирается целиком, а не из JSX-кусков: иначе между
                        подписью и двоеточием появлялся лишний пробел */}
                    <span className={classes.cardMetaClamp}>
                        {`${t('coursesPage.card.result')}: ${pick(course.finalProduct)}`}
                    </span>
                </div>
            </div>

            {hint && (
                <div className={classes.cardHint}>
                    <IntlMessage id="coursesPage.search.foundInProgram"/>:{' '}
                    <mark>{hint.n}. {hint.title}</mark>
                </div>
            )}

            <div className={classes.cardDivider}/>

            <div className={classes.cardFoot}>
                <div className={classes.cardTrainer}>
                    <span className={classes.cardAvatar}>
                        {photo
                            ? <img src={photo} alt={pick(course.trainer.name)}/>
                            : <UserOutlined/>}
                    </span>
                    <span className={classes.cardTrainerName}>{pick(course.trainer?.name)}</span>
                </div>
                <span className={classes.cardMore}>
                    <IntlMessage id="coursesPage.card.more"/>
                    <ArrowRightOutlined/>
                </span>
            </div>
        </Link>
    );

    if (reduced) return card;

    // Каскад по карточкам, а не <Fade cascade> на сетке: cascade оборачивает
    // каждого потомка в свой div и ломает CSS-grid.
    return (
        <Fade triggerOnce direction="up" duration={500} delay={index * 40} fraction={0.15}>
            {card}
        </Fade>
    );
};

export default CourseCard;
